import { type HTMLAttributes, type ReactNode } from 'react';
import styles from './sub-section.module.scss';
import LinkButton from 'components/controls/links/link-button';
import ChevronR from 'assets/icons/chevron-r.svg';
import MarkdownParser from 'components/rich-text/markdown-parser';
import Divider from '../divider';
import {
	getActionAttributes,
	getContextAttributes,
	type AnalyticsContext,
} from 'lib/analytics/analyticsAttributes';
/*
	Generic "Sub-Section" component that includes Title + standard "sub-component" spacing.
*/
const Subsection = ({
	title = null,
	children,
	hasClampedWidth = true,
	hasDivider,
	hasTopDivider,
	description,
	link,
	analyticsContext,
	...restProps
}: IProps) => {
	const classes = hasClampedWidth ? styles.clampedWidthWrapper : styles.wrapper;

	return (
		<section
			{...restProps}
			{...getContextAttributes(analyticsContext)}
			className={`${classes} ${restProps?.className || ''}`}
		>
			{hasTopDivider && <Divider />}
			{title && (
				<div className={styles.title}>
					<div className={styles.titleAndLinkWrapper}>
						<h3 className={styles.headerTitle}>
							<MarkdownParser>{title}</MarkdownParser>
						</h3>
						{link?.url && link?.cta ? (
							<div className={styles.cta}>
								<LinkButton
									href={link.url}
									text={link.cta}
									rightIcon={<ChevronR />}
									{...getActionAttributes({
										contentName: link.cta,
									})}
								/>
							</div>
						) : null}
					</div>
					{!!description && (
						<div className={styles.description}>
							<MarkdownParser>{description}</MarkdownParser>
						</div>
					)}
				</div>
			)}
			<>{children}</>
			{hasDivider && <Divider />}
		</section>
	);
};

interface IProps extends HTMLAttributes<HTMLElement> {
	children: ReactNode;
	description?: string;
	hasClampedWidth?: boolean;
	hasDivider?: boolean;
	hasTopDivider?: boolean;
	link?: { cta: string; url: string };
	analyticsContext?: AnalyticsContext;
	title?: string;
}

export default Subsection;
export type { IProps as SubsectionProps };
